import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from "react-router-dom";
import { withTranslation } from 'react-i18next';
import { ProgressBar } from 'primereact/progressbar';
import axios from "axios";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import currencyFormatter from "currency-formatter";
import { Timeline } from "primereact/timeline";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Badge } from 'primereact/badge';

const SettingsDashboardComponent = (props) => {
  const { t } = props;
  const { id } = useParams();
  const [summary, setSummary] = useState({});
  const [chartsReady, setChartsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const apiCall = (method, url, data) => {
    return axios({
      method: method,
      url: process.env.REACT_APP_API_HOST + url,
      data,
      params: {},
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    });
  };

  const colorCache = useMemo(() => new Map(), []);
  const generateColor = useCallback((key) => {
    if (colorCache.has(key)) return colorCache.get(key);
    const c = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    colorCache.set(key, c);
    return c;
  }, [colorCache]);

  const formatMoney = (value) => {
    if (typeof value === 'string') return value;
    if (typeof value !== 'number') return value ?? '';
    return currencyFormatter.format(value, {
      precision: 0,
      symbol: '',
      decimal: ',',
      thousand: ' ',
    });
  };

  const getLgColClass = (count) => {
    if (count === 1) return 'p-col-12';
    if (count === 2) return 'p-col-12 p-lg-6';
    if (count === 3) return 'p-col-12 p-lg-4';
    if (count === 4) return 'p-col-12 p-lg-3';
    return 'p-col-12 p-lg-2';
  };

  const measureContainer = useCallback(() => {
    const w = containerRef.current?.clientWidth ?? 0;
    setContainerWidth(w);
    return w;
  }, []);

  useEffect(() => {
    const dashboardId = props.dashboardId
      ?? window.App?.urlVars()?.summaryId
      ?? id;
    setSummary({});
    setChartsReady(false);
    if (!dashboardId) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    apiCall('get', '/api/v2/dashboard/' + dashboardId)
      .then(response => {
        if (!active) return;
        setSummary(response.data.data);
        setChartsReady(false);
        const tryReady = () => {
          const w = measureContainer();
          if (w > 300) {
            setChartsReady(true);
            return;
          }
          requestAnimationFrame(tryReady);
        };
        requestAnimationFrame(tryReady);
      })
      .catch(error => {
        if (!active) return;
        if (error.response?.status === 404) {
          return;
        }
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [props.dashboardId, id]);

  useEffect(() => {
    const onResize = () => {
      measureContainer();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [measureContainer]);

  const renderKpi = (chart, key) => {
    const theme = chart.subtitle_theme;
    const arrowIcon =
      theme === 'success' ? 'pi pi-arrow-up' :
        theme === 'danger' ? 'pi pi-arrow-down' :
          theme === 'warning' ? 'pi pi-exclamation-triangle' : null;
    return (
      <div key={key} className={"p-my-1"}>
        <Card className={"p-p-2 p-shadow-1"} style={{ borderRadius: 12 }}>
          <div className={"p-d-flex p-jc-between p-ai-start"}>
            <div>
              <small className={"p-text-uppercase p-text-secondary"} style={{ letterSpacing: 0.6 }}>
                {chart.title}
              </small>
              <div className={"p-mt-1"} style={{ fontSize: 22, fontWeight: 700 }}>
                {chart.amount}
              </div>
            </div>
            <div className={"p-ml-2"} style={{ opacity: 0.85 }}>
              <i className={"pi pi-" + chart.icon} style={{ fontSize: '1.6em' }} />
            </div>
          </div>
          <div className={"p-mt-2"}>
            <span className={"text-" + (theme || '')}>
              <span className={"p-text-bold"}>
                {arrowIcon && <i className={arrowIcon}></i>}
                {arrowIcon && <>&nbsp;</>}
                {chart.subtitle}
              </span>
              {chart.description ? <> {chart.description}</> : null}
            </span>
          </div>
        </Card>
      </div>
    );
  };

  const buildBarOrPieDatasetsFromObject = (chart) => {
    const safeData = (chart?.data && typeof chart.data === 'object' && !Array.isArray(chart.data))
      ? chart.data
      : {};
    const type = chart.type === 'pie' ? 'pie' : 'bar';
    const datasets = Object.entries(safeData).map(([label, value]) => ({
      type,
      label: `${label} (${formatMoney(value)}${chart.unit ? ` ${chart.unit}` : ''})`,
      backgroundColor: generateColor(`${chart.title || ''}-${label}`),
      data: [value]
    }));
    return { labels: [''], datasets };
  };

  const ChartLoader = ({ height = 300 }) => (
    <div
      className="p-d-flex p-ai-center p-jc-center"
      style={{ height }}
    >
      <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.5rem', opacity: 0.6 }} />
    </div>
  );

  const renderBarOrPie = (chart, key) => {
    const chartType = chart.type === 'pie' ? 'pie' : 'bar';
    const data = buildBarOrPieDatasetsFromObject(chart);

    return (
      <div key={key} className={"p-my-1"}>
        <Card className={"p-p-2 p-shadow-1"} style={{ borderRadius: 12 }}>
          <div className={"p-d-flex p-jc-between p-ai-center p-mb-2"}>
            <div>
              <small className={"p-text-uppercase p-text-secondary"} style={{ letterSpacing: 0.6 }}>
                {chart.title}
              </small>
            </div>
            {chart.icon && (
              <i className={"pi pi-" + chart.icon} style={{ fontSize: '1.2em', opacity: 0.75 }} />
            )}
          </div>
          <div style={{ height: 300 }}>
            {(chartsReady && containerWidth > 300) ? (
              <Chart
                type={chartType}
                data={data}
                style={{ width: '100%', height: '300px' }}
                options={{
                  maintainAspectRatio: false,
                  indexAxis: 'y',
                  plugins: {
                    legend: { labels: { color: '#000' } }
                  },
                  scales: chartType === 'bar' ? {
                    x: { ticks: { color: '#444' }, grid: { color: '#f3f3f3' } },
                    y: { ticks: { color: '#444' }, grid: { color: '#f3f3f3' } }
                  } : undefined
                }}
              />
            ) : (
              <ChartLoader height={300} />
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderBarStacked = (chart, key) => {
    const rows = Array.isArray(chart?.data) ? chart.data : [];
    const labels = rows.map((_, i) => `${i + 1}.`);
    const allKeys = [];
    rows.forEach(obj => {
      if (obj && typeof obj === 'object') {
        Object.keys(obj).forEach(k => {
          if (!allKeys.includes(k)) allKeys.push(k);
        });
      }
    });
    const datasets = allKeys.map(k => ({
      label: k,
      backgroundColor: generateColor(`${chart.title || ''}-${k}`),
      data: rows.map(r => (r && typeof r === 'object' && r[k] != null) ? r[k] : 0)
    }));

    return (
      <div key={key} className={"p-my-1"}>
        <Card className={"p-p-2 p-shadow-1"} style={{ borderRadius: 12 }}>
          <div className={"p-d-flex p-jc-between p-ai-center p-mb-2"}>
            <div>
              <small className={"p-text-uppercase p-text-secondary"} style={{ letterSpacing: 0.6 }}>
                {chart.title}
              </small>
            </div>
            {chart.icon && (
              <i className={"pi pi-" + chart.icon} style={{ fontSize: '1.2em', opacity: 0.75 }} />
            )}
          </div>
          <div style={{ height: 300 }}>
            {(chartsReady && containerWidth > 300) ? (
              <Chart
                type="bar"
                data={{ labels, datasets }}
                style={{ width: '100%', height: '300px' }}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { labels: { color: '#000' } },
                    tooltip: {
                      callbacks: chart.unit ? {
                        label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y)} ${chart.unit}`
                      } : undefined
                    }
                  },
                  scales: {
                    x: { stacked: true, ticks: { color: '#444' }, grid: { color: '#f3f3f3' } },
                    y: { stacked: true, ticks: { color: '#444' }, grid: { color: '#f3f3f3' } }
                  }
                }}
              />
            ) : (
              <ChartLoader height={300} />
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderTimeline = (chart, key) => {
    const events = Array.isArray(chart?.events) ? chart.events : [];
    const _events = [];
    events.forEach((event, idx) => {
      _events.push({ label: event.title, original: event });
      if (idx === events.length - 1) {
        _events.push({ label: '', original: '' });
      }
    });
    return (
      <div key={key} className={"p-my-1"}>
        <Card className={"p-p-2 p-shadow-1"} style={{ borderRadius: 12 }}>
          <div className={"p-d-flex p-jc-between p-ai-center p-mb-2"}>
            <small className={"p-text-uppercase p-text-secondary"} style={{ letterSpacing: 0.6 }}>
              {chart.title || t('timeline') || 'Timeline'}
            </small>
          </div>
          <Timeline
            value={_events}
            layout="horizontal"
            align="top"
            marker={(marker) => (
              <span className={"text-" + marker?.original?.theme}>
                <i
                  className={"pi pi-" + marker?.original?.icon}
                  style={{
                    fontSize: '1.5rem',
                    position: 'relative',
                    zIndex: 9,
                    borderRadius: '100%'
                  }}
                />
              </span>
            )}
            content={(content) => (
              <>
                {content?.original?.date && (
                  <>
                    {window.App.formatDateValue(content.original.date)}
                    <br />
                  </>
                )}
                <strong className={"p-d-block"}>{content?.original?.title}</strong>
                {content?.original?.description}
              </>
            )}
          />
        </Card>
      </div>
    );
  };

  const renderTable = (chart, key) => {
    const columns = chart.data?.columns || [];
    const rows = chart.data?.rows || [];
    const summary = chart.data?.summary;

    const bodyTemplate = (rowData, col) => {
      const cellData = rowData[col.key];
      if (!cellData) return null;
      const value = typeof cellData === 'object' ? cellData.value : cellData;
      const theme = cellData?.theme;
      const unit = col.unit ? ` ${col.unit}` : '';

      if (theme) {
        return (
          <div className="p-d-flex p-ai-center">
            <span 
              className="p-mr-2" 
              style={{ 
                height: '12px', 
                width: '12px', 
                backgroundColor: theme === 'success' ? '#22C55E' : '#EF4444', 
                borderRadius: '50%',
                display: 'inline-block',
                flexShrink: 0
              }} 
            />
            <span>{value}{value !== "" && unit}</span>
          </div>
        );
      }
      return <span>{value != null ? `${value}${unit}` : ''}</span>;
    };
    const footerGroup = summary ? (
      <ColumnGroup>
        <Row>
          {columns.map((col, i) => {
            const sumCell = summary[col.key];
            const val = typeof sumCell === 'object' ? sumCell?.value : sumCell;
            const unit = col.unit ? ` ${col.unit}` : '';
            const theme = sumCell?.theme;
            return (
              <Column 
                key={'footer-' + col.key} 
                style={col.dataType === 'number' ? { textAlign: 'right' } : { textAlign: 'left' }}
                footer={() => (
                  <div className={`p-text-bold ${theme ? 'p-d-flex p-ai-center' : ''}`} style={{ fontWeight: 700 }}>
                    {theme && (
                      <span 
                        className="p-mr-2" 
                        style={{ 
                          height: '12px', width: '12px', 
                          backgroundColor: theme === 'success' ? '#22C55E' : '#EF4444', 
                          borderRadius: '50%', display: 'inline-block' 
                        }} 
                      />
                    )}
                    {val != null ? `${val}${unit}` : ''}
                  </div>
                )} 
              />
            );
          })}
        </Row>
      </ColumnGroup>
    ) : null;

    return (
      <div key={key} className="p-my-1">
        <Card className="p-p-2 p-shadow-1" style={{ borderRadius: 12 }}>
          <div className="p-d-flex p-jc-between p-ai-center p-mb-3">
            <small className="p-text-uppercase p-text-secondary" style={{ letterSpacing: 0.6 }}>
              {chart.title}
            </small>
            {chart.icon && <i className={"pi pi-" + chart.icon} style={{ opacity: 0.75 }} />}
          </div>
          <DataTable 
            value={rows} 
            responsiveLayout="scroll"
            className="p-datatable-sm dashboard-data-table"
            emptyMessage={t('noResults')}
            footerColumnGroup={footerGroup}
        >
            {columns.map((col) => (
              <Column 
                key={col.key} 
                field={col.key} 
                header={col.label} 
                body={(rowData) => bodyTemplate(rowData, col)}
                alignHeader={col.dataType === 'number' ? 'right' : 'left'}
                className={col.dataType === 'number' ? 'dashboard-number-column' : 'dashboard-text-column'}
                style={col.dataType === 'number' ? { textAlign: 'right' } : { textAlign: 'left' }}
              />
            ))}
            {summary && (
              <ColumnGroup type="footer">
                <Row>
                  {columns.map((col) => {
                    const sumCell = summary[col.key];
                    const val = typeof sumCell === 'object' ? sumCell?.value : sumCell;
                    const unit = col.unit ? ` ${col.unit}` : '';
                    const theme = sumCell?.theme;

                    return (
                      <Column 
                        key={'footer-' + col.key} 
                        style={(col.dataType === 'number' || col.key === 'missingCheckins') ? { textAlign: 'right' } : { textAlign: 'left' }}
                        footer={() => (
                          <div className={`p-text-bold ${theme ? 'p-d-flex p-ai-center p-jc-end' : ''}`} style={{ fontWeight: 700 }}>
                            {theme && (
                              <span 
                                className="p-mr-2" 
                                style={{ 
                                  height: '12px', width: '12px', 
                                  backgroundColor: theme === 'success' ? '#22C55E' : '#EF4444', 
                                  borderRadius: '50%', display: 'inline-block' 
                                }} 
                              />
                            )}
                            {val != null ? `${val}${unit}` : ''}
                          </div>
                        )} 
                      />
                    );
                  })}
                </Row>
              </ColumnGroup>
            )}
          </DataTable>
        </Card>
      </div>
    );
  };

  const renderVisualization = (node, key) => {
    switch (node?.type) {
      case 'kpi':
        return renderKpi(node, key);

      case 'bar':
      case 'pie':
        return renderBarOrPie(node, key);

      case 'bar_stacked':
        return renderBarStacked(node, key);

      case 'timeline':
        return renderTimeline(node, key);

      case 'table':
        return renderTable(node, key);

      default:
        return null;
    }
  };

  const renderNode = (node, key = 'root') => {
    if (!node) return null;
    if (node.type === 'verticalLayout') {
      return (
        <div key={key} className={"p-d-flex p-flex-column"}>
          {node.elements?.map((child, i) => renderNode(child, `${key}-v-${i}`))}
        </div>
      );
    }
    if (node.type === 'horizontalLayout') {
      const count = node.elements?.length ?? 0;
      const colClass = getLgColClass(count);
      return (
        <div key={key} className={"p-grid"}>
          {node.elements?.map((child, i) => (
            <div key={`${key}-hcol-${i}`} className={colClass}>
              {renderNode(child, `${key}-h-${i}`)}
            </div>
          ))}
        </div>
      );
    }
    return renderVisualization(node, key);
  };

  return (
    <div ref={containerRef} className={"settings-dashboard widget"} style={{ position: 'relative' }}>
      {loading && (
        <ProgressBar
          mode="indeterminate"
          style={{ height: '5px', position: "absolute", top: 0, left: 0, right: 0, borderRadius: 0 }}
        />
      )}
      {summary?.name && (
        <div className={"p-grid p-ai-center p-mb-2"}>
          <div className={"p-col-12 p-lg-6"}>
            <h2 className={"p-m-0 p-p-0 text-primary"} style={{ fontWeight: 800 }}>
              {summary.name}
            </h2>
            {summary?.status && (
              <small className={"p-text-secondary"}>
                {/* {summary?.computed_at ? ` • ${summary.computed_at}` : ''} */}
                {t('lastUpdatedAt')}: {summary?.computed_at && (
                  <Badge value={summary.computed_at}
                    className={ summary.status === 'READY' ? 'p-badge-success' : summary.status === 'FAILED' ? 'p-badge-danger' : 'p-badge-warning' }
                  />
                )}
              </small>
            )}
          </div>
          <div className={"p-col-12 p-lg-6 p-text-right"} />
        </div>
      )}
      {summary?.visualizations ? (
        <div className={"p-mt-1"}>
          {renderNode(summary.visualizations, 'dashboard-root')}
        </div>
      ) : (
        !loading && <small className={"p-text-secondary"}>{t('reportUnavailable')}</small>
      )}
    </div>
  );
};

export default withTranslation('common')(SettingsDashboardComponent);
